import type { RenderMode } from "@magic-md/core";
import { useT } from "@magic-md/i18n/react";
import { AlertTriangle } from "lucide-react";
import { getThemeAccentStyle } from "../../../theme/runtime/theme-accents";
import { RenderUnitBadge, type RenderUnitBadgeProps } from "./RenderUnitBadge";

export function TemplateUsageWarning({
    unitId,
    kindLabel,
    technicalLabel,
    params,
    mode,
    badge,
    locationLabel,
}: {
    unitId: string;
    kindLabel: string;
    technicalLabel?: string | null;
    params: string[];
    mode: RenderMode;
    badge: Pick<RenderUnitBadgeProps, "main" | "sub" | "sup" | "template">;
    locationLabel: string;
}) {
    const t = useT("editor");
    const accentStyle = getThemeAccentStyle("warning");
    const refs = params.map((param) => `$ref{input/${param}}`);
    const message = t("render.templateUsage.message", { location: locationLabel });
    const missingTemplateLabel = t("render.templateUsage.missingTemplate");

    if (mode === "inline") {
        return (
            <span
                className="inline-flex items-center gap-1 rounded border border-dashed px-1.5 py-0.5 text-[10px] theme-accent-banner"
                style={accentStyle}
                data-template-usage-warning="true"
                title={message}
            >
                <RenderUnitBadge {...badge} size="sm" />
                <AlertTriangle size={10} aria-hidden="true" />
                <code className="font-mono">{unitId}</code>
                <span>{missingTemplateLabel}</span>
            </span>
        );
    }

    return (
        <div
            className="my-1.5 rounded-lg border border-dashed px-3 py-2.5 theme-accent-banner"
            style={accentStyle}
            data-template-usage-warning="true"
        >
            <div className="flex min-w-0 items-start gap-3">
                <RenderUnitBadge {...badge} className="mt-0.5 bg-background" />
                <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-mono text-[13px] font-medium text-foreground">
                            {unitId}
                        </span>
                        <span
                            className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium theme-accent-chip"
                            style={accentStyle}
                        >
                            {missingTemplateLabel}
                        </span>
                    </div>
                    <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                        <span className="shrink-0">{kindLabel}</span>
                        {technicalLabel ? (
                            <>
                                <span className="text-muted-foreground/40">·</span>
                                <span
                                    className="hidden min-w-0 truncate font-mono text-muted-foreground/50 sm:inline"
                                    title={technicalLabel}
                                >
                                    {technicalLabel}
                                </span>
                            </>
                        ) : null}
                    </div>
                    <div
                        className="mt-1 truncate text-[11px] theme-accent-text"
                        style={accentStyle}
                        title={message}
                    >
                        {message}
                    </div>
                    <div className="mt-2 flex min-w-0 flex-wrap gap-1">
                        {refs.map((ref) => (
                            <code
                                key={ref}
                                className="rounded border border-border bg-background/70 px-1.5 py-0.5 text-[10px] text-muted-foreground"
                            >
                                {ref}
                            </code>
                        ))}
                    </div>
                </div>
                <AlertTriangle
                    size={15}
                    className="shrink-0 theme-accent-text"
                    style={accentStyle}
                    aria-label={missingTemplateLabel}
                />
            </div>
        </div>
    );
}
